import React, { useCallback, useEffect, useRef, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import { StatusBadge } from "../../components/ui";
import { usePanelStyles } from "../../components/QueueIntegrationModal/panelKit";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";

const COLUMNS = 6;

const useStyles = makeStyles(() => ({
  table: { minWidth: 760 },
  count: { fontSize: 13 },
}));

const isOnline = (device) => {
  if (device.status) return String(device.status).toLowerCase() === "online";
  return !!device.online;
};

const DevicesTab = () => {
  const classes = useStyles();
  const panel = usePanelStyles();
  const { datetimeToClient } = useDate();

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hotspot/devices");
      if (!isMountedRef.current) return;
      setDevices(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const onlineCount = devices.filter(isOnline).length;

  return (
    <>
      <div className={panel.toolbar}>
        {!loading && devices.length > 0 && (
          <span className={`${panel.cellMuted} ${classes.count}`}>
            {onlineCount} / {devices.length} {i18n.t("hotspot.devices.status.online").toLowerCase()}
          </span>
        )}
        <div className={panel.toolbarSpacer} />
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          disabled={loading}
          onClick={() => fetchDevices()}
        >
          {i18n.t("hotspot.devices.refresh")}
        </Button>
      </div>

      <Paper className={`${panel.tableCard} ${panel.tableCardAuto}`} elevation={0}>
        {!loading && devices.length === 0 ? (
          <div className={panel.empty}>{i18n.t("hotspot.devices.empty")}</div>
        ) : (
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("hotspot.devices.table.name")}</TableCell>
                <TableCell>{i18n.t("hotspot.devices.table.slug")}</TableCell>
                <TableCell>{i18n.t("hotspot.devices.table.status")}</TableCell>
                <TableCell>{i18n.t("hotspot.devices.table.lastContact")}</TableCell>
                <TableCell>{i18n.t("hotspot.devices.table.vpn")}</TableCell>
                <TableCell>{i18n.t("hotspot.devices.table.modes")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device) => {
                const online = isOnline(device);
                const lastContact = device.ultimoContato || device.lastSeen || device.lastSeenAt || device.updatedAt;
                const modes = Array.isArray(device.modosAcesso) ? device.modosAcesso.join(", ") : "-";
                return (
                  <TableRow key={device.id || device.slug}>
                    <TableCell className={panel.cellStrong}>{device.nome || "-"}</TableCell>
                    <TableCell className={panel.mono}>{device.slug || "-"}</TableCell>
                    <TableCell>
                      <StatusBadge
                        status={online ? "online" : "offline"}
                        label={
                          online
                            ? i18n.t("hotspot.devices.status.online")
                            : i18n.t("hotspot.devices.status.offline")
                        }
                      />
                    </TableCell>
                    <TableCell>{lastContact ? datetimeToClient(lastContact) : "-"}</TableCell>
                    <TableCell className={panel.mono}>{device.vpnIp || "-"}</TableCell>
                    <TableCell>{modes || "-"}</TableCell>
                  </TableRow>
                );
              })}
              {loading && <TableRowSkeleton columns={COLUMNS} />}
            </TableBody>
          </Table>
        )}
      </Paper>
    </>
  );
};

export default DevicesTab;
